import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';
import ImageUploadField from './ImageUploadField';

const slugify = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const emptyForm = {
  title: '', slug: '', excerpt: '', content: '',
  category: 'stories', cover_image: '', author: 'Apeksha',
};

const CATEGORIES = [
  { value: 'myths', label: 'Myths' },
  { value: 'symbolism', label: 'Symbolism' },
  { value: 'stories', label: 'Stories' },
  { value: 'aftercare', label: 'Aftercare' },
];

const BlogManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/blog`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setMode('create');
    setForm(emptyForm);
    setCurrent(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setMode('edit');
    setCurrent(item);
    setForm({
      title: item.title || '',
      slug: item.slug || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      category: item.category || 'stories',
      cover_image: item.cover_image || '',
      author: item.author || 'Apeksha',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/blog/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    const payload = { ...form };
    if (!payload.slug && payload.title) payload.slug = slugify(payload.title);
    try {
      if (mode === 'create') {
        await axios.post(`${API}/blog`, payload, getAuthHeader());
        toast.success('Blog post added');
      } else {
        await axios.put(`${API}/blog/${current.id}`, payload, getAuthHeader());
        toast.success('Blog post updated');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="blog-content">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <FileText size={28} /> Blog Posts
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-blog-btn"
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gray-800"
        >
          <Plus size={16} /> Add Blog Post
        </button>
      </div>

      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="bg-white border border-gray-200 p-6 flex gap-6" data-testid={`blog-item-${item.id}`}>
            {item.cover_image && (
              <img src={item.cover_image} alt={item.title} className="w-32 h-32 object-cover flex-shrink-0" />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-heading text-xl font-bold">{item.title}</h3>
                {item.category && (
                  <span className="font-mono text-[10px] uppercase bg-gray-100 px-2 py-1">{item.category}</span>
                )}
              </div>
              <p className="font-body text-gray-600 text-sm mb-4 line-clamp-2">{item.excerpt}</p>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-gray-500">{item.author}</span>
                {item.created_at && (
                  <span className="font-mono text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <button onClick={() => openEdit(item)} className="p-2 bg-gray-100 rounded hover:bg-gray-200" data-testid={`edit-blog-${item.id}`}>
                <Edit size={18} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200" data-testid={`delete-blog-${item.id}`}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`${mode === 'create' ? 'Add' : 'Edit'} Blog Post`}
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.title || !form.content || !form.excerpt || !form.category}
            data-testid="blog-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Add Blog Post' : 'Save Changes'}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })}
              data-testid="blog-form-title"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Slug *</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              data-testid="blog-form-slug"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Category *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="blog-form-category"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <ImageUploadField
            value={form.cover_image}
            onChange={(url) => setForm({ ...form, cover_image: url })}
            label="Cover Image"
            testId="blog-image"
          />
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Excerpt *</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              data-testid="blog-form-excerpt"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Content *</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={6}
              data-testid="blog-form-content"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Author</label>
            <input
              type="text"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              data-testid="blog-form-author"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BlogManager;
